import { toast } from 'react-toastify';
import React, { useState, useEffect, useCallback } from 'react';
import { Pencil, Trash2, Plus } from 'lucide-react';
import { bicycleFloorService, bicycleTypeService, bicycleAddonService, bicycleSpaceService } from '../services/bicycleService';
import { BicycleSpace, BicycleType, BicycleAddon } from '../types/bicycle';
import FloorLayoutViewer from './FloorLayoutViewer';
import FabricFloorLayoutEditor from './FabricFloorLayoutEditor';
import BicycleSpaceEditModal from './BicycleSpaceEditModal';
import BulkSpaceModal from './BulkSpaceModal';
import TypeLegend from './TypeLegend';
import { FloorSpace, SpaceUpdateData } from '../types/floorLayout';
import '../styles/ModelDetailsSlider.css';
import '../styles/DataTable.css';

interface BicycleFloor {
  _id: string;
  name: string;
  projectId: string;
  projectName?: string;
  layout?: {
    url: string;
    key?: string;
    width: number;
    height: number;
    size?: number;
    mimeType?: string;
  };
  spaceDefaults?: {
    width: number;
    height: number;
  };
  spaces?: {
    total?: number;
    available?: number;
    marketingHold?: number;
    salesHold?: number;
    sold?: number;
    customerSelection?: number;
    customerSubmitted?: number;
  };
  // Fallback for flat structure (if API returns it differently)
  total?: number;
  available?: number;
  marketingHold?: number;
  salesHold?: number;
  sold?: number;
  customerSelection?: number;
  customerSubmitted?: number;
}

interface BicycleFloorDetailsSliderProps {
  workspaceId: string;
  bicycleFloorId: string;
  onClose: () => void;
}

type TabType = 'floor-layout' | 'bicycle-spaces';

const BicycleFloorDetailsSlider: React.FC<BicycleFloorDetailsSliderProps> = ({
  workspaceId,
  bicycleFloorId,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('floor-layout');
  const [bicycleFloor, setBicycleFloor] = useState<BicycleFloor | null>(null);
  const [bicycleSpaces, setBicycleSpaces] = useState<BicycleSpace[]>([]);
  const [bicycleTypes, setBicycleTypes] = useState<BicycleType[]>([]);
  const [bicycleAddons, setBicycleAddons] = useState<BicycleAddon[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingSpaces, setLoadingSpaces] = useState(false);
  const [error, setError] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState<BicycleSpace | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);

  useEffect(() => {
    fetchBicycleFloor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bicycleFloorId]);

  useEffect(() => {
    if (activeTab === 'bicycle-spaces' || activeTab === 'floor-layout') {
      fetchBicycleSpaces();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    // Disable body scroll when modal is open
    document.body.style.overflow = 'hidden';

    return () => {
      // Re-enable body scroll when modal closes
      document.body.style.overflow = '';
    };
  }, []);

  const fetchBicycleFloor = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await bicycleFloorService.getById(workspaceId, bicycleFloorId);
      setBicycleFloor(response.bicycleFloor);

      // Fetch bicycle types and addons for the project - await both to ensure they load before rendering
      if (response.bicycleFloor.projectId) {
        await Promise.all([
          fetchBicycleTypes(response.bicycleFloor.projectId),
          fetchBicycleAddons(response.bicycleFloor.projectId)
        ]);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch bicycle floor details');
      console.error('Error fetching bicycle floor:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBicycleTypes = async (projectId: string) => {
    try {
      const response = await bicycleTypeService.getAll(workspaceId, {
        projectId,
        limit: 1000
      });
      setBicycleTypes(response.bicycleTypes || []);
    } catch (err: any) {
      console.error('Error fetching bicycle types:', err);
    }
  };

  const fetchBicycleAddons = async (projectId: string) => {
    try {
      const response = await bicycleAddonService.getAll(workspaceId, {
        projectId,
        limit: 1000
      });
      setBicycleAddons(response.bicycleAddons || []);
    } catch (err: any) {
      console.error('Error fetching bicycle addons:', err);
    }
  };

  const fetchBicycleSpaces = async () => {
    try {
      setLoadingSpaces(true);
      const { bicycleSpaceService } = await import('../services/bicycleService');
      const response = await bicycleSpaceService.getByFloor(workspaceId, bicycleFloorId);
      setBicycleSpaces(response.bicycleSpaces);
    } catch (err: any) {
      console.error('Error fetching bicycle spaces:', err);
    } finally {
      setLoadingSpaces(false);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'AVAILABLE':
        return 'status-badge badge-available';
      case 'MARKETING HOLD':
        return 'status-badge badge-marketing-hold';
      case 'SALES HOLD':
        return 'status-badge badge-sales-hold';
      case 'CUSTOMER SELECTION':
        return 'status-badge badge-customer-selection';
      case 'CUSTOMER SUBMITTED':
        return 'status-badge badge-customer-submitted';
      case 'SOLD':
        return 'status-badge badge-sold';
      default:
        return 'status-badge';
    }
  };

  const handleSavePositions = async (updates: SpaceUpdateData[]) => {
    try {
      const { bicycleSpaceService } = await import('../services/bicycleService');
      // Batch update bicycle space positions
      const updateData = updates.map(update => ({
        id: update.id,
        spaceObject: update.spaceObject
      }));
      await bicycleSpaceService.bulkUpdate(workspaceId, updateData);
      // Refresh spaces and bicycle floor data
      await fetchBicycleSpaces();
      await fetchBicycleFloor();
      // Don't close editor automatically after save
    } catch (err: any) {
      console.error('Error saving positions:', err);
      throw err;
    }
  };

  const handleSpaceClick = (spaceId: string) => {
    const space = bicycleSpaces.find(s => s._id === spaceId);
    if (space) {
      handleEditSpace(space);
    }
  };

  const handleViewSpaceClick = useCallback((spaceId: string) => {
    const space = bicycleSpaces.find(s => s._id === spaceId);
    if (space) {
      setSelectedSpace(space);
      setShowViewModal(true);
    }
  }, [bicycleSpaces]);

  const handleEditSpace = (space: BicycleSpace) => {
    setSelectedSpace(space);
    setShowEditModal(true);
  };

  const handleDeleteSpace = async (spaceId: string) => {
    if (!window.confirm('Are you sure you want to delete this bicycle space?')) {
      return;
    }

    try {
      const { bicycleSpaceService } = await import('../services/bicycleService');
      await bicycleSpaceService.delete(workspaceId, spaceId);
      await refreshAllData();
    } catch (err: any) {
      console.error('Error deleting space:', err);
      toast.error(err.response?.data?.message || 'Failed to delete bicycle space');
    }
  };

  const handleSpaceSaved = async () => {
    await refreshAllData();
    setShowEditModal(false);
  };

  const refreshAllData = async () => {
    await Promise.all([fetchBicycleFloor(), fetchBicycleSpaces()]);
  };

  const handleBulkSpaceSave = async (spaces: any[]) => {
    // Create all spaces using the bicycle space service
    for (const spaceData of spaces) {
      await bicycleSpaceService.create(workspaceId, spaceData);
    }
    await refreshAllData();
  };

  if (loading) {
    return (
      <div className="model-details-slider">
        <div className="model-details-loading">
          <div className="builders-loading-spinner" />
        </div>
      </div>
    );
  }

  if (error || !bicycleFloor) {
    return (
      <div className="model-details-slider">
        <div className="model-details-error">
          <p>{error || 'Bicycle floor not found'}</p>
          <button className="btn btn-secondary btn-md" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="model-details-slider">
      {/* Header */}
      <div className="model-details-header">
        <div className="model-details-header-left">
          <button className="model-details-back-btn" onClick={onClose}>
            ← Back
          </button>
          <div className="model-details-title">
            <h2>Bicycle Floor: {bicycleFloor.name}</h2>
            <p>Project: {bicycleFloor.projectName || 'N/A'}</p>
          </div>
        </div>
        <button className="model-details-close-btn" onClick={onClose}>
          ✕
        </button>
      </div>

      {/* Tabs */}
      <div style={{
        background: '#D4CBC4',
        padding: '20px',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', gap: '8px', padding: 0 }}>
          <button
            onClick={() => setActiveTab('floor-layout')}
            style={{
              background: activeTab === 'floor-layout' ? '#48182F' : 'transparent',
              border: 'none',
              outline: 'none',
              color: activeTab === 'floor-layout' ? '#FFFFFF' : '#6B7280',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: activeTab === 'floor-layout' ? '600' : '500',
              cursor: 'pointer',
              borderRadius: '20px',
              transition: 'all 0.2s',
              boxShadow: 'none'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'floor-layout') {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.5)';
                e.currentTarget.style.color = '#48182F';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'floor-layout') {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#6B7280';
              }
            }}
          >
            Floor Layout
          </button>
          <button
            onClick={() => setActiveTab('bicycle-spaces')}
            style={{
              background: activeTab === 'bicycle-spaces' ? '#48182F' : 'transparent',
              border: 'none',
              outline: 'none',
              color: activeTab === 'bicycle-spaces' ? '#FFFFFF' : '#6B7280',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: activeTab === 'bicycle-spaces' ? '600' : '500',
              cursor: 'pointer',
              borderRadius: '20px',
              transition: 'all 0.2s',
              boxShadow: 'none'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'bicycle-spaces') {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.5)';
                e.currentTarget.style.color = '#48182F';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'bicycle-spaces') {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#6B7280';
              }
            }}
          >
            Bicycle Spaces
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="model-details-content" style={{
        gridTemplateColumns: '1fr',
        padding: '24px',
        overflow: 'auto',
        background: '#D4CBC4'
      }}>
        {activeTab === 'floor-layout' && (
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Floor Layout</h3>
              <button
                onClick={() => setShowEditor(true)}
                className="btn btn-primary btn-sm"
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <Pencil size={16} /> Edit Floor Layout
              </button>
            </div>
            <div className="model-details-tile-content">
              {loadingSpaces ? (
                <div style={{
                  padding: '40px',
                  textAlign: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '400px'
                }}>
                  <div className="builders-loading-spinner" />
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div style={{ flex: 1 }}>
                    <FloorLayoutViewer
                      layoutImage={bicycleFloor?.layout}
                      spaceDefaults={bicycleFloor?.spaceDefaults}
                      spaces={bicycleSpaces as FloorSpace[]}
                      parkingTypes={bicycleTypes as any}
                      onSpaceClick={handleViewSpaceClick}
                    />
                  </div>
                  <div style={{
                    width: '280px',
                    borderLeft: '1px solid #E5E7EB',
                    paddingLeft: '20px'
                  }}>
                    <h4 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '16px'
                    }}>
                      Type Legend
                    </h4>
                    <TypeLegend parkingTypes={bicycleTypes as any} iconType="bicycle" />

                    {bicycleAddons.length > 0 && (
                      <>
                        <h4 style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#374151',
                          marginTop: '24px',
                          marginBottom: '16px'
                        }}>
                          Addon Legend
                        </h4>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '12px'
                        }}>
                          {bicycleAddons.map((addon) => (
                            <div key={addon._id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: addon.color || '#FFA500',
                                border: '1.5px solid #ffffff',
                                boxShadow: '0 0 0 1px #D1D5DB',
                                flexShrink: 0
                              }} />
                              <span style={{ fontSize: '13px', color: '#374151' }}>
                                {addon.name}
                              </span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'bicycle-spaces' && (
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <div style={{ flex: 1 }}>
                <h3>Bicycle Spaces</h3>
                <div style={{ fontSize: '14px', color: '#6B7280', fontWeight: '400' }}>
                  Total: {bicycleFloor.spaces?.total || bicycleFloor.total || 0} |
                  Available: {bicycleFloor.spaces?.available || bicycleFloor.available || 0} |
                  Sold: {bicycleFloor.spaces?.sold || bicycleFloor.sold || 0}
                </div>
              </div>
              <button
                onClick={() => setShowBulkModal(true)}
                className="btn btn-primary btn-sm"
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <Plus size={16} /> Bulk Add Spaces
              </button>
            </div>
            <div className="model-details-tile-content" style={{ padding: 0 }}>
              {loadingSpaces ? (
                <div style={{
                  padding: '40px',
                  textAlign: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <div className="builders-loading-spinner" />
                </div>
              ) : bicycleSpaces.length === 0 ? (
                <div className="model-details-empty">
                  <p>No bicycle spaces on this floor</p>
                </div>
              ) : (
                <div className="data-table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>
                          Space Number
                        </th>
                        <th>
                          Type
                        </th>
                        <th>
                          Status
                        </th>
                        <th>
                          Premium
                        </th>
                        <th>
                          Assigned Unit
                        </th>
                        <th>
                          Addons
                        </th>
                        <th style={{
                          width: '120px'
                        }}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {bicycleSpaces.map((space, index) => (
                        <tr
                          key={space._id}
                          style={{
                            borderBottom: '1px solid #E5E7EB',
                            backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F5F3F0'
                          }}
                        >
                          <td className="data-table-primary-cell">
                            {space.number}
                          </td>
                          <td style={{ color: '#374151' }}>
                            {space.type}
                          </td>
                          <td>
                            <span className={getStatusBadgeClass(space.status)}>
                              {space.status}
                            </span>
                          </td>
                          <td style={{ color: '#374151' }}>
                            {space.isPremium ? (
                              <span style={{
                                color: '#48182F',
                                fontWeight: '600'
                              }}>
                                Yes
                              </span>
                            ) : (
                              <span style={{ color: '#9CA3AF' }}>No</span>
                            )}
                          </td>
                          <td style={{ color: '#374151' }}>
                            {space.unitNo || '-'}
                          </td>
                          <td style={{ color: '#374151' }}>
                            {space.addons && space.addons.length > 0 ? (
                              <div style={{ fontSize: '13px' }}>
                                {space.addons.map((addon, idx) => (
                                  <span key={addon._id}>
                                    {addon.name}
                                    {addon.mandatory && <span style={{ color: '#48182F', marginLeft: '4px' }}>*</span>}
                                    {idx < space.addons!.length - 1 && ', '}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span style={{ color: '#9CA3AF' }}>-</span>
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', width: '100px' }}>
                              <button
                                onClick={() => handleEditSpace(space)}
                                className="btn btn-icon btn-sm"
                                style={{
                                  padding: '6px',
                                  color: '#48182F',
                                  border: 'none',
                                  background: 'transparent'
                                }}
                                title="Edit Space"
                              >
                                <Pencil size={18} />
                              </button>
                              <button
                                onClick={() => handleDeleteSpace(space._id)}
                                className="btn btn-icon btn-sm"
                                style={{
                                  padding: '6px',
                                  color: '#DC2626',
                                  border: 'none',
                                  background: 'transparent'
                                }}
                                title="Delete Space"
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Floor Layout Editor Modal */}
      {showEditor && (
        <FabricFloorLayoutEditor
          workspaceId={workspaceId}
          floorId={bicycleFloorId}
          projectId={bicycleFloor?.projectId || ''}
          entityType="bicycle"
          floorName={bicycleFloor?.name || 'Bicycle Floor'}
          projectName={bicycleFloor?.projectName || 'Project'}
          layoutImage={bicycleFloor?.layout}
          spaceDefaults={bicycleFloor?.spaceDefaults || { width: 150, height: 75 }}
          parkingTypes={bicycleTypes as any}
          parkingAddons={bicycleAddons as any}
          spaces={bicycleSpaces as FloorSpace[]}
          onClose={() => setShowEditor(false)}
          onSave={handleSavePositions}
          onSpaceClick={handleSpaceClick}
        />
      )}

      {/* Bicycle Space Edit Modal */}
      {showEditModal && selectedSpace && (
        <BicycleSpaceEditModal
          isOpen={showEditModal}
          workspaceId={workspaceId}
          projectId={bicycleFloor?.projectId || ''}
          space={selectedSpace}
          onClose={() => {
            setShowEditModal(false);
            setSelectedSpace(null);
          }}
          onSave={handleSpaceSaved}
          onDelete={handleDeleteSpace}
        />
      )}

      {/* Bulk Space Modal */}
      {showBulkModal && (
        <BulkSpaceModal
          isOpen={showBulkModal}
          workspaceId={workspaceId}
          projectId={bicycleFloor?.projectId || ''}
          floorId={bicycleFloorId}
          entityType="bicycle"
          spaceDefaults={bicycleFloor?.spaceDefaults}
          layoutDimensions={bicycleFloor?.layout ? { width: bicycleFloor.layout.width, height: bicycleFloor.layout.height } : undefined}
          existingSpaces={bicycleSpaces}
          onClose={() => setShowBulkModal(false)}
          onSave={handleBulkSpaceSave}
        />
      )}

      {/* Space Details View Modal */}
      {showViewModal && selectedSpace && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 9999,
            }}
            onClick={() => {
              setShowViewModal(false);
              setSelectedSpace(null);
            }}
          />
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              zIndex: 10000,
              minWidth: '450px',
              maxWidth: '500px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#48182F' }}>
                Space Details
              </h3>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedSpace(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#6B7280',
                  padding: '4px',
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Space Number */}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                <span style={{ color: '#6B7280', fontSize: '14px' }}>Space Number</span>
                <span style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>{selectedSpace.number}</span>
              </div>

              {/* Type */}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                <span style={{ color: '#6B7280', fontSize: '14px' }}>Type</span>
                <span style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>{selectedSpace.type}</span>
              </div>

              {/* Status */}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                <span style={{ color: '#6B7280', fontSize: '14px' }}>Status</span>
                <span className={getStatusBadgeClass(selectedSpace.status)}>{selectedSpace.status}</span>
              </div>

              {/* Premium */}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                <span style={{ color: '#6B7280', fontSize: '14px' }}>Premium</span>
                <span style={{ fontWeight: 600, fontSize: '14px', color: selectedSpace.isPremium ? '#48182F' : '#9CA3AF' }}>
                  {selectedSpace.isPremium ? 'Yes' : 'No'}
                </span>
              </div>

              {/* Assigned Unit */}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                <span style={{ color: '#6B7280', fontSize: '14px' }}>Assigned Unit</span>
                <span style={{ fontWeight: 600, fontSize: '14px', color: selectedSpace.unitNo ? '#111827' : '#9CA3AF' }}>
                  {selectedSpace.unitNo || '-'}
                </span>
              </div>

              {/* Addons */}
              {selectedSpace.addons && selectedSpace.addons.length > 0 && (
                <div style={{ padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                  <span style={{ color: '#6B7280', fontSize: '14px', display: 'block', marginBottom: '8px' }}>Add-ons</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {selectedSpace.addons.map((addon) => (
                      <span
                        key={addon._id}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 10px',
                          backgroundColor: 'white',
                          borderRadius: '16px',
                          fontSize: '13px',
                          color: '#374151',
                          border: '1px solid #E5E7EB',
                        }}
                      >
                        {addon.color && (
                          <span
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: addon.color,
                            }}
                          />
                        )}
                        {addon.name}
                        {addon.mandatory && (
                          <span style={{ color: '#48182F', fontWeight: 600 }}>*</span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Mandatory Addons */}
              {selectedSpace.mandatoryAddons && selectedSpace.mandatoryAddons.length > 0 && (
                <div style={{ padding: '12px', backgroundColor: '#FEF3C7', borderRadius: '8px' }}>
                  <span style={{ color: '#92400E', fontSize: '14px', display: 'block', marginBottom: '8px' }}>Mandatory Add-ons</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {selectedSpace.mandatoryAddons.map((addon) => (
                      <span
                        key={addon._id}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 10px',
                          backgroundColor: 'white',
                          borderRadius: '16px',
                          fontSize: '13px',
                          color: '#92400E',
                          border: '1px solid #FCD34D',
                        }}
                      >
                        {addon.color && (
                          <span
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: addon.color,
                            }}
                          />
                        )}
                        {addon.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedSpace(null);
                }}
                className="btn btn-secondary btn-md"
                style={{ padding: '8px 20px' }}
              >
                Close
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BicycleFloorDetailsSlider;
